import React, { useState, useEffect } from 'react';
import {
  Container,
  ThemeTable,
  ThemeInput,
  UserDetailModal,
  DogByIDModal,
  EditDogModal,
} from '../../components/components';
import {
  useGetUsersQuery,
  useLazyGetUserByIdQuery,
} from '../../services/users';
import { useLazyGetDogByIDQuery } from '../../services/dogsApi';
import { users_white_icon } from '../../assets/icons/icons';
import { Switch } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import EditUserModal from '../../components/editUserModal/editUserModal';
import { sortDogsByName } from '../../helpers/helpers';

const Users = () => {
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isDogModalOpen, setIsDogModalOpen] = useState(false);
  const [isEditDogModalOpen, setIsEditDogModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultPerPage, setResultPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [unpaidOnly, setUnpaidOnly] = useState(false);

  // Debounce search input - wait 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      if (searchInput !== search) {
        setCurrentPage(1);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, refetch } = useGetUsersQuery({
    currentPage,
    resultPerPage,
    search,
    unpaidOnly,
  });

  const [getUserById] = useLazyGetUserByIdQuery();
  const [getDogById, { isLoading: fetchingDog }] = useLazyGetDogByIDQuery();

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDog, setSelectedDog] = useState(null);

  useEffect(() => {
    setTotal(data?.data[0]?.total || 0);
  }, [data]);

  const onOpenUserModal = async id => {
    const response = await getUserById({ id: id });
    setSelectedUser(response.data?.data || null);
    setIsUserModalOpen(true);
  };

  const onEditUser = async id => {
    const response = await getUserById({ id: id });
    setEditUserData(response.data?.data?.user || null);
    setIsEditUserModalOpen(true);
  };

  const onOpenDogModal = async dogId => {
    const response = await getDogById(dogId);
    setSelectedDog(response.data?.data || null);
    setIsDogModalOpen(true);
  };

  const onEditDog = () => {
    setIsDogModalOpen(false);
    setIsEditDogModalOpen(true);
  };

  const handleEditDogSuccess = () => {
    setIsEditDogModalOpen(false);
    setSelectedDog(null);
    refetch();
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text, record) => (
        <div
          key={`full-name-${record._id}`}
          className="flex items-center justify-center gap-2"
        >
          <p
            className="font-medium text-primary cursor-pointer hover:underline"
            onClick={() => onOpenUserModal(record._id)}
          >
            {text}
          </p>
          <button
            onClick={() => onEditUser(record._id)}
            className="p-1 text-gray-400 hover:text-primary hover:bg-gray-100 rounded transition-colors"
            title="Edit user"
          >
            <EditOutlined />
          </button>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (text, record) => (
        <div
          key={`active-${record._id}`}
          className="flex justify-center gap-2 items-center"
        >
          <span
            className={`px-2 py-1 text-xs font-medium rounded ${
              text !== false
                ? 'bg-green-100 text-green-600'
                : 'bg-red-100 text-red-600'
            }`}
          >
            {text !== false ? 'Active' : 'Disabled'}
          </span>
          {record.isSuperAdmin && (
            <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-700 border border-purple-300">
              Admin
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Dogs',
      dataIndex: 'dogs',
      key: 'dogs',
      render: (dogs, record) => (
        <div
          key={`dogs-${record._id}`}
          className="flex items-center justify-center gap-1 flex-wrap max-w-xs"
        >
          {dogs && dogs.length > 0 ? (
            // Sorted before slicing so the three shown are the first three
            // alphabetically, not whichever three the API happened to return.
            sortDogsByName(dogs)
              .slice(0, 3)
              .map(dog => (
                <div
                  key={dog._id}
                  className="flex items-center gap-1 bg-gray-50 rounded-full px-2 py-1 border border-gray-200 cursor-pointer hover:bg-gray-100 hover:border-primary transition-colors"
                  title={`${dog.dog_name} - ${dog.breed || 'Unknown breed'}`}
                  onClick={() => onOpenDogModal(dog._id)}
                >
                  <img
                    src={dog.avatar}
                    alt={dog.dog_name}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span className="text-xs font-medium text-gray-700">
                    {dog.dog_name}
                  </span>
                </div>
              ))
          ) : (
            <span className="text-xs text-gray-400">No dogs</span>
          )}
          {dogs && dogs.length > 3 && (
            <span className="text-xs font-medium text-gray-500">
              +{dogs.length - 3} more
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Unpaid',
      dataIndex: 'totalUnpaidPrice',
      key: 'totalUnpaidPrice',
      render: (text, record) => {
        const amount = text || 0;
        return (
          <p
            key={`unpaid-${record._id}`}
            className={`font-medium text-center ${amount > 0 ? 'text-red-600' : 'text-green-600'}`}
          >
            ${amount.toFixed(2)}
          </p>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text, record) => (
        <a
          key={`email-${record._id}`}
          href={`mailto:${text}`}
          className="text-center font-medium text-primary hover:underline block"
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Contact',
      dataIndex: 'contact',
      key: 'contact',
      render: (text, record) => (
        <p key={`contact-${record._id}`} className="text-center font-medium">
          {text}
        </p>
      ),
    },
  ];

  return (
    <Container
      selected={'4'}
      header={
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-primary h-9 w-9 rounded-full flex justify-center items-center">
              <img src={users_white_icon} width={17} height={17} alt="" />
            </div>
            <p className="ml-2 font-semibold text-2xl">Owners</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Unpaid Only</span>
              <Switch
                checked={unpaidOnly}
                onChange={checked => {
                  setUnpaidOnly(checked);
                  setCurrentPage(1);
                }}
                className="bg-gray-400"
              />
            </div>
            <ThemeInput
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className={'w-80'}
              placeholder={'Search'}
            />
            {searchInput && (
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setCurrentPage(1);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      }
    >
      <ThemeTable
        pagination={{ total, setCurrentPage, setResultPerPage }}
        columns={columns}
        data={data?.data[0]?.users || []}
        loader={isLoading}
        entityName="owners"
        emptyText={
          search
            ? `No owners match "${search}".`
            : unpaidOnly
              ? 'No owners have an outstanding balance.'
              : 'No owners yet. They appear here once someone registers in the app.'
        }
      />

      <UserDetailModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        data={selectedUser}
        onRefresh={async () => {
          // Refetch the current user's data to update the modal
          if (selectedUser?.user?._id) {
            const response = await getUserById({ id: selectedUser.user._id });
            setSelectedUser(response.data?.data || null);
          }
          // Also refetch the users list to update the table
          refetch();
        }}
      />

      <EditUserModal
        isOpen={isEditUserModalOpen}
        onClose={() => setIsEditUserModalOpen(false)}
        data={editUserData}
        onSuccess={() => {
          setIsEditUserModalOpen(false);
          refetch();
        }}
      />

      <DogByIDModal
        isOpen={isDogModalOpen}
        onClose={() => {
          setIsDogModalOpen(false);
          setSelectedDog(null);
        }}
        data={selectedDog}
        isLoading={fetchingDog}
        onEditDog={onEditDog}
        onDeleteSuccess={() => {
          setIsDogModalOpen(false);
          setSelectedDog(null);
          refetch();
        }}
      />

      <EditDogModal
        isOpen={isEditDogModalOpen}
        onClose={() => {
          setIsEditDogModalOpen(false);
          setSelectedDog(null);
        }}
        data={selectedDog}
        onSuccess={handleEditDogSuccess}
      />
    </Container>
  );
};

export default Users;
